import { useState, useEffect, useContext, useCallback } from "react";
import { Container, EntityType, EntityFactory, APIOutput, ContentEvaluationConfiguration, EntityRef, Page, PageDraft, IPage } from "@amzn/ask-legal-domain";
import { AppContext } from "../setup/context";
import type { PageEvaluationConfiguration } from "../components/page/PageContentEvaluation";

enum LoadingStep {
    Configuration = "configuration",
    PageData = "pageData",
    ContainerMetadata = "containerMetadata",
    Complete = "complete"
}

export const usePageEvaluation = (pageRef?: EntityRef, draftRef?: EntityRef, pageId?: string) => {
    const context = useContext(AppContext);
    const [configuration, setConfiguration] = useState<PageEvaluationConfiguration | null>(null);
    const [containerMetadata, setContainerMetadata] = useState<Record<string, Container.Data>>({});
    const [containerRefs, setContainerRefs] = useState<EntityRef[]>([]);
    const [selectedContainerIds, setSelectedContainerIds] = useState<string[]>([]);
    const [pageName, setPageName] = useState<string>("");
    const [pageData, setPageData] = useState<Page.Data>(null);
    const [draftData, setDraftData] = useState<PageDraft.Data>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingStep, setLoadingStep] = useState<LoadingStep>(LoadingStep.Configuration);
    const [isDeprecated, setIsDeprecated] = useState<boolean>(false);

    const entityRef = pageRef || draftRef;

    const loadConfiguration = useCallback(async () => {
        const configId = pageId || entityRef?.id;
        if (!configId) {
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
            return;
        }

        try {
            setLoadingStep(LoadingStep.Configuration);
            const rawOutput = await context.getContentEvaluationConfigurationAPI().load(configId);
            const output = APIOutput.fromRaw<ContentEvaluationConfiguration.Data>(rawOutput.data);

            if (output.isErr()) {
                if (output.err?.code !== 404) {
                    console.error("Failed to load configuration:", output.err);
                }
                setConfiguration(null);
                setLoadingStep(LoadingStep.Complete);
                setIsLoading(false);
                return;
            }

            const configDataOutput = output.data;
            if (configDataOutput) {
                const config: PageEvaluationConfiguration = {
                    evaluationGranularity: configDataOutput.evaluationGranularity ===
                        ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage ? "Page" : "Container",
                    containerIds: configDataOutput.containerIds
                };
                setConfiguration(config);
                setIsDeprecated(configDataOutput.deprecated ?? false);

                // Set default selected containers for Container-level evaluation
                if (config.evaluationGranularity === "Container" && configDataOutput.containerIds.length > 0) {
                    setSelectedContainerIds([configDataOutput.containerIds[0]]);
                }
            } else {
                setConfiguration(null);
                setIsDeprecated(false);
            }
            setLoadingStep(LoadingStep.PageData);
        } catch (error) {
            console.error("Failed to load configuration:", error);
            setConfiguration(null);
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
        }
    }, [pageId, entityRef?.id, context]);

    const loadPageData = useCallback(async () => {
        if (!entityRef) {
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
            return;
        }

        try {
            if (pageRef) {
                const result = await context.getPageAPI().load2(entityRef.id);
                const output = APIOutput.fromRaw<IPage.LoadPageOutput>(result.data);
                if (!output.isErr()) {
                    setPageData(output.data.page);
                    setPageName(output.data.page.title);

                    // Extract container refs
                    const refs = output.data.page.sections.flatMap((section: Page.Section) =>
                        section.containers.map((container: Container.Data) => ({
                            id: container.id,
                            version: container.version,
                            type: EntityType.Container
                        }))
                    );
                    setContainerRefs(refs);
                } else {
                    console.error("Failed to load page data:", output.err.toString());
                    setLoadingStep(LoadingStep.Complete);
                    setIsLoading(false);
                }
            } else if (draftRef) {
                const result = await context.getPageDraftAPI().load(entityRef.id);
                const output = APIOutput.fromRaw<PageDraft.Data>(result.data);
                if (!output.isErr()) {
                    setDraftData(output.data);
                    setPageName(output.data.newPageTitle);

                    // Extract container refs
                    const refs = output.data.newSections.flatMap((section: Page.Section) =>
                        section.containers.map((container: Container.Data) => ({
                            id: container.id,
                            version: container.version,
                            type: EntityType.Container
                        }))
                    );
                    setContainerRefs(refs);
                } else {
                    console.error("Failed to load draft data:", output.err.toString());
                    setLoadingStep(LoadingStep.Complete);
                    setIsLoading(false);
                }
            }
        } catch (error) {
            console.error("Failed to load page/draft data:", error);
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
        }
    }, [entityRef, pageRef, draftRef, context]);

    const loadContainerMetadata = useCallback(async (containerRefs: EntityRef[]) => {
        if (!containerRefs?.length) {
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
            return;
        }

        try {
            setLoadingStep(LoadingStep.ContainerMetadata);
            const rawOutput = await context.getContainerAPI().batchLoadMetadata(containerRefs);
            const output = APIOutput.fromRaw<Container.Data[]>(rawOutput.data);

            if (output.isErr()) {
                console.error("Failed to load container metadata:", output.err.toString());
                setLoadingStep(LoadingStep.Complete);
                setIsLoading(false);
                return;
            }

            const metadata: Record<string, Container.Data> = {};
            output.data.forEach(container => {
                metadata[container.id] = container;
            });
            setContainerMetadata(metadata);
        } catch (error) {
            console.error("Failed to load container metadata:", error);
            setLoadingStep(LoadingStep.Complete);
            setIsLoading(false);
        }
    }, [context]);

    useEffect(() => {
        const runSequentialLoading = async () => {
            await loadConfiguration();
            await loadPageData();
        };

        runSequentialLoading();
    }, [loadConfiguration, loadPageData]);

    // After page data loads, check if we need container metadata based on configuration
    useEffect(() => {
        if (loadingStep === LoadingStep.PageData && configuration && (pageData || draftData)) {
            if (configuration.evaluationGranularity === "Page") {
                setLoadingStep(LoadingStep.Complete);
                setIsLoading(false);
            } else if (configuration.evaluationGranularity === "Container" && containerRefs.length > 0) {
                // Container-level evaluation with containers - load metadata
                loadContainerMetadata(containerRefs).then(() => {
                    setLoadingStep(LoadingStep.Complete);
                    setIsLoading(false);
                });
            } else {
                setLoadingStep(LoadingStep.Complete);
                setIsLoading(false);
            }
        }
    }, [loadingStep, configuration, pageData, draftData, containerRefs, loadContainerMetadata]);

    // Only show button if we successfully loaded a real ContentEvaluationConfiguration
    const hasConfiguration = configuration !== null;
    const isPageLevelEvaluation = configuration?.evaluationGranularity === "Page";
    const availableContainerIds = configuration?.containerIds || containerRefs.map(ref => ref.id);

    return {
        configuration,
        hasConfiguration,
        isPageLevelEvaluation,
        containerMetadata,
        containerRefs,
        availableContainerIds,
        selectedContainerIds,
        setSelectedContainerIds,
        pageName,
        isLoading,
        isDeprecated,
        reloadConfiguration: loadConfiguration,
        entityRef: entityRef!
    };
};
